/**
 * Client mirror of the Ablefy gate + value extraction in
 * apps/api/src/lib/ablefy.ts — powers the sample preview in AblefyPanel
 * (WebhookEndpointDetail.tsx). Keep in sync. This only extracts the subset
 * of fields the preview actually renders (deal_value, deal_cash_in,
 * currency, order_id, email, product_name) — not the full MappedEvent
 * (keys/fields/custom_data) the server produces.
 */

function str(p: Record<string, unknown>, key: string): string | undefined {
  const v = p[key];
  if (typeof v === "string") return v.trim() || undefined;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return undefined;
}

/** Make/Zapier can wrap the payload in a single-element array — mirror of ablefy.ts unwrap(). */
function unwrap(payload: unknown): unknown {
  return Array.isArray(payload) && payload.length > 0 ? payload[0] : payload;
}

function obj(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : undefined;
}

/**
 * Mirrors ABLEFY_TRACKED_EVENTS in apps/api/src/lib/ablefy.ts — keep in
 * sync (including the naming-pattern caveat on the two unconfirmed
 * subscription event values documented there).
 */
const ABLEFY_TRACKED_EVENTS = new Set([
  "order.one_time.paid",
  "order.installment.paying",
  "order.subscription.paying",
  "order.limited_subscription.paying",
]);

/**
 * Mirrors ablefyRefundType + refundAmount in apps/api/src/lib/ablefy.ts
 * (TASK-199, unconfirmed event names) — keep in sync.
 */
function ablefyRefundType(payload: unknown): "refund" | "chargeback" | undefined {
  const event = str(obj(unwrap(payload)) ?? {}, "event")?.toLowerCase();
  if (!event || !event.startsWith("order.")) return undefined;
  if (event.includes("chargeback")) return "chargeback";
  if (event.includes("refund")) return "refund";
  return undefined;
}

function negative(v: string | undefined): string | undefined {
  if (v === undefined) return undefined;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? String(-Math.abs(n)) : undefined;
}

function refundAmount(p: Record<string, unknown>): string | undefined {
  for (const key of ["refund_amount", "refunded_amount", "amount_refunded"]) {
    const n = negative(str(p, key));
    if (n !== undefined) return n; // first readable field wins
  }
  return undefined;
}

export function ablefyShouldTrack(payload: unknown): {
  track: boolean;
  reason: string;
} {
  const p = obj(unwrap(payload)) ?? {};
  const event = str(p, "event") ?? "";
  if (ablefyRefundType(p)) {
    return { track: true, reason: `event="${event}" — wird als Refund erfasst` };
  }
  return ABLEFY_TRACKED_EVENTS.has(event)
    ? { track: true, reason: `event="${event}"` }
    : {
        track: false,
        reason: `event="${event}" — nur Kauf-Events werden getrackt`,
      };
}

export function ablefyValues(payload: unknown): {
  deal_value?: string;
  deal_cash_in?: string;
  currency?: string;
  order_id?: string;
  email?: string;
  product_name?: string;
} {
  const p = obj(unwrap(payload)) ?? {};
  const payer = obj(p["payer"]) ?? {};
  const product = obj(p["product"]) ?? {};
  const out: ReturnType<typeof ablefyValues> = {};

  if (ablefyRefundType(p)) {
    const refunded = refundAmount(p);
    const dealValue = negative(str(p, "order_amount_gross")) ?? refunded;
    if (dealValue) out.deal_value = dealValue;
    if (refunded) out.deal_cash_in = refunded;
  } else {
    const dealValue = str(p, "order_amount_gross");
    if (dealValue) out.deal_value = dealValue;

    const schedule = p["payments_schedule"];
    const firstInstalment = Array.isArray(schedule) ? obj(schedule[0]) : undefined;
    const cashIn = firstInstalment ? str(firstInstalment, "amount") : undefined;
    if (cashIn) out.deal_cash_in = cashIn;
  }

  const currency = str(p, "currency");
  if (currency) out.currency = currency;
  const orderId = str(p, "order_id");
  if (orderId) out.order_id = orderId;
  const email = str(payer, "email");
  if (email) out.email = email;
  const productName = str(product, "name");
  if (productName) out.product_name = productName;
  return out;
}
